import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Radio } from '../Radio';
import { Tooltip } from '../Tooltip';

import type { ReviewFilesProps, ReviewFileItem } from './ReviewFiles.types';

/**
 * Selection mode for file cards
 * - 'single': Radio button selection (one file at a time)
 * - 'multiple': Checkbox selection (multiple files)
 */
export type SelectionMode = 'single' | 'multiple';

/**
 * Default file size formatter
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/**
 * Get file extension from filename
 */
const getFileExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) return '';
  return filename.slice(lastDotIndex + 1).toLowerCase();
};

/**
 * Check if file is an image based on extension
 */
const isImageFile = (filename: string): boolean => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico', 'tiff', 'tif'];
  return imageExtensions.includes(getFileExtension(filename));
};

/**
 * Get file icon color based on file type - EXACT copy from FileCard.tsx
 */
const getFileIconColor = (filename: string): { bg: string; fg: string } => {
  const ext = getFileExtension(filename);

  if (isImageFile(filename)) return { bg: '#E8F5E9', fg: '#2E7D32' };

  switch (ext) {
    case 'pdf':
      return { bg: '#FFEBEE', fg: '#C62828' };
    case 'doc':
    case 'docx':
    case 'odt':
    case 'rtf':
    case 'txt':
      return { bg: '#E3F2FD', fg: '#1565C0' };
    case 'ppt':
    case 'pptx':
    case 'pptm':
    case 'key':
    case 'odp':
      return { bg: '#FFF3E0', fg: '#E65100' };
    case 'html':
    case 'htm':
    case 'xml':
      return { bg: '#F3E5F5', fg: '#7B1FA2' };
    case 'srt':
    case 'vtt':
      return { bg: '#E0F7FA', fg: '#00838F' };
    default:
      return { bg: '#F5F5F5', fg: '#616161' };
  }
};

/**
 * Get file icon symbol based on file type - EXACT copy from FileCard.tsx
 */
const getFileIconSymbol = (filename: string): string => {
  const ext = getFileExtension(filename);

  if (isImageFile(filename)) return '🖼';

  switch (ext) {
    case 'pdf':
      return 'PDF';
    case 'doc':
    case 'docx':
      return 'DOC';
    case 'ppt':
    case 'pptx':
    case 'pptm':
      return 'PPT';
    case 'txt':
      return 'TXT';
    case 'html':
    case 'htm':
      return 'HTML';
    default:
      return ext.toUpperCase().slice(0, 3) || 'FILE';
  }
};

// EXACT styled components from FileCard.tsx

const CardContainer = styled.div<{
  $isSelected: boolean;
  $hasError: boolean;
  $isDisabled?: boolean;
}>`
  ${({ theme, $isSelected, $hasError, $isDisabled }) => css`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background-color: ${$isDisabled
      ? theme.colors.background.default
      : $isSelected
        ? `color-mix(in srgb, ${theme.colors.highlight} 8%, white)`
        : theme.colors.white};
    border: 1px solid
      ${$hasError
        ? theme.colors.status.error
        : $isDisabled
          ? theme.colors.border.light
          : $isSelected
            ? theme.colors.highlight
            : theme.colors.border.light};
    border-radius: 8px;
    cursor: ${$isDisabled || $hasError ? 'not-allowed' : 'pointer'};
    transition: all 0.15s ease;
    opacity: ${$isDisabled ? 0.6 : 1};

    &:hover {
      background-color: ${$isDisabled
        ? theme.colors.background.default
        : $isSelected
          ? `color-mix(in srgb, ${theme.colors.highlight} 8%, white)`
          : theme.colors.background.default};
      border-color: ${$hasError
        ? theme.colors.status.error
        : $isDisabled
          ? theme.colors.border.light
          : $isSelected
            ? theme.colors.highlight
            : theme.colors.border.medium};
    }
  `}
`;

const StyledCheckbox = styled.div<{ $checked: boolean; $disabled?: boolean }>`
  ${({ theme, $checked, $disabled }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border: 2px solid
      ${$disabled
        ? theme.colors.border.medium
        : $checked
          ? theme.colors.highlight
          : theme.colors.border.medium};
    border-radius: 4px;
    background-color: ${$disabled
      ? theme.colors.background.default
      : $checked
        ? theme.colors.highlight
        : 'transparent'};
    cursor: ${$disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.15s ease;
    flex-shrink: 0;

    &:hover {
      border-color: ${$disabled ? theme.colors.border.medium : theme.colors.highlight};
    }

    svg {
      display: ${$checked ? 'block' : 'none'};
      color: white;
    }
  `}
`;

const CheckIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 6L5 9L10 3" />
  </svg>
);

const FileIconContainer = styled.div<{ $bgColor: string; $fgColor: string }>`
  ${({ theme, $bgColor, $fgColor }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background-color: ${$bgColor};
    color: ${$fgColor};
    font-size: 10px;
    font-weight: ${theme.typography.fontWeight.bold};
    letter-spacing: 0.5px;
    flex-shrink: 0;
  `}
`;

const FileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const FileName = styled.span`
  ${({ theme }) => css`
    font-size: ${theme.typography.fontSize.sm};
    font-weight: ${theme.typography.fontWeight.medium};
    color: ${theme.colors.text.primary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.3;
  `}
`;

const FileDetails = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    color: ${theme.colors.text.muted};
  `}
`;

const FileSizeTag = styled.span`
  ${({ theme }) => css`
    display: inline-flex;
    padding: 2px 6px;
    background-color: #f5f5f5;
    border-radius: 4px;
    font-weight: ${theme.typography.fontWeight.medium};
  `}
`;

const FileSourcePath = styled.span`
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
`;

const FileError = styled.div`
  ${({ theme }) => css`
    font-size: 12px;
    color: ${theme.colors.status.error};
  `}
`;

const RemoveButton = styled.button`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
    color: ${theme.colors.text.muted};
    border-radius: 4px;
    flex-shrink: 0;
    transition: all 0.15s ease;

    &:hover {
      color: ${theme.colors.status.error};
      background-color: #ffebee;
    }

    &:focus-visible {
      outline: 2px solid ${theme.colors.highlight};
      outline-offset: 1px;
    }

    svg {
      display: block;
    }
  `}
`;

const CloseIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 4L12 12M12 4L4 12" />
  </svg>
);

const Container = styled.div<{ $maxHeight?: number | string }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  ${({ $maxHeight }) =>
    $maxHeight &&
    css`
      max-height: ${typeof $maxHeight === 'number' ? `${$maxHeight}px` : $maxHeight};
      overflow-y: auto;
    `}
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EmptyState = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    text-align: center;
    color: ${theme.colors.text.secondary};
    font-family: ${theme.typography.fontFamily};
    font-size: ${theme.typography.fontSize.sm};
  `}
`;

/**
 * FileCard - Single file card matching web-app-risk-reviews FileCard.tsx exactly
 */
interface FileCardProps {
  file: ReviewFileItem;
  isSelected?: boolean;
  onSelect?: () => void;
  onRemove?: () => void;
  selectionMode?: SelectionMode;
  isDisabled?: boolean;
  onToggleSelect?: (fileId: string) => void;
  showSize?: boolean;
  showSource?: boolean;
  showErrors?: boolean;
  formatSize?: (bytes: number) => string;
  dataTestId?: string;
}

const FileCard: React.FC<FileCardProps> = ({
  file,
  isSelected = false,
  onSelect,
  onRemove,
  selectionMode = 'single',
  isDisabled = false,
  onToggleSelect,
  showSize = true,
  showSource = false,
  showErrors = true,
  formatSize: customFormatSize,
  dataTestId,
}) => {
  const hasError = !!file.validationError;
  const isMultiMode = selectionMode === 'multiple';
  const isInteractionDisabled = hasError || (isMultiMode && isDisabled && !isSelected);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isInteractionDisabled) return;

    if (isMultiMode && file.id && onToggleSelect) {
      onToggleSelect(file.id);
    } else {
      onSelect?.();
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (isInteractionDisabled) return;

      if (isMultiMode && file.id && onToggleSelect) {
        onToggleSelect(file.id);
      } else {
        onSelect?.();
      }
    }
  };

  const iconColors = getFileIconColor(file.name);
  const iconSymbol = getFileIconSymbol(file.name);
  const sizeFormatter = customFormatSize || formatFileSize;

  const renderSelectionControl = () => {
    if (isMultiMode) {
      return (
        <StyledCheckbox
          $checked={isSelected && !hasError}
          $disabled={isInteractionDisabled}
          role="checkbox"
          aria-checked={isSelected}
          aria-label={`Select ${file.name}`}
        >
          <CheckIcon />
        </StyledCheckbox>
      );
    }

    return (
      <Radio
        value={file.name}
        checked={isSelected && !hasError}
        disabled={hasError}
        aria-label={`Select ${file.name}`}
      />
    );
  };

  return (
    <CardContainer
      $isSelected={isSelected}
      $hasError={hasError}
      $isDisabled={isMultiMode && isDisabled && !isSelected}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="option"
      aria-selected={isSelected}
      aria-disabled={isInteractionDisabled}
      data-testid={dataTestId}
    >
      {renderSelectionControl()}

      <FileIconContainer $bgColor={iconColors.bg} $fgColor={iconColors.fg} aria-hidden="true">
        {iconSymbol}
      </FileIconContainer>

      <FileInfo>
        <Tooltip content={file.name} onlyWhenOverflow position="top">
          <FileName>{file.name}</FileName>
        </Tooltip>
        {hasError && showErrors ? (
          <FileError>{file.validationError?.message}</FileError>
        ) : (
          <FileDetails>
            {showSize && <FileSizeTag>{sizeFormatter(file.size)}</FileSizeTag>}
            {showSource && file.sourcePath && (
              <Tooltip content={file.sourcePath} onlyWhenOverflow position="top">
                <FileSourcePath>{file.sourcePath}</FileSourcePath>
              </Tooltip>
            )}
          </FileDetails>
        )}
      </FileInfo>

      {onRemove && (
        <Tooltip content="Remove file" position="top">
          <RemoveButton onClick={handleRemove} aria-label={`Remove ${file.name}`}>
            <CloseIcon />
          </RemoveButton>
        </Tooltip>
      )}
    </CardContainer>
  );
};

/**
 * ReviewFiles Component
 *
 * Display and manage file list for review with selection support.
 * Uses the exact FileCard styling from web-app-risk-reviews.
 */
export const ReviewFiles: React.FC<ReviewFilesProps> = ({
  files,
  selectionEnabled = false,
  selectionMode = 'single',
  selectedIds,
  maxSelection,
  onSelectionChange,
  onRemove,
  showRemove,
  showSize = true,
  showSource = false,
  showErrors = true,
  emptyState,
  maxHeight,
  formatSize: customFormatSize,
  dataTestId,
  dataId,
  className,
  style,
}) => {
  const shouldShowRemove = showRemove ?? !!onRemove;

  // Normalize selectedIds to array
  const normalizeSelectedIds = (): string[] => {
    if (!selectedIds) return [];
    return Array.isArray(selectedIds) ? selectedIds : [selectedIds];
  };

  const selectedIdsArray = normalizeSelectedIds();

  const isSelected = (fileId: string): boolean => {
    return selectedIdsArray.includes(fileId);
  };

  const handleSelect = (fileId: string) => {
    if (!onSelectionChange) return;
    onSelectionChange([fileId]);
  };

  const handleToggleSelect = (fileId: string) => {
    if (!onSelectionChange) return;

    if (selectionMode === 'single') {
      onSelectionChange([fileId]);
    } else {
      if (isSelected(fileId)) {
        onSelectionChange(selectedIdsArray.filter((id) => id !== fileId));
      } else {
        // Check if we've reached max selection
        if (maxSelection && selectedIdsArray.length >= maxSelection) {
          return;
        }
        onSelectionChange([...selectedIdsArray, fileId]);
      }
    }
  };

  if (files.length === 0) {
    return (
      <Container
        $maxHeight={maxHeight}
        data-testid={dataTestId}
        data-id={dataId}
        className={className}
        style={style}
      >
        {emptyState || <EmptyState>No files added</EmptyState>}
      </Container>
    );
  }

  return (
    <Container
      $maxHeight={maxHeight}
      data-testid={dataTestId}
      data-id={dataId}
      className={className}
      style={style}
    >
      <FileList role="listbox" aria-label="Files for review">
        {files.map((file) => {
          const selected = isSelected(file.id);
          const isFileDisabled =
            selectionMode === 'multiple' &&
            maxSelection !== undefined &&
            selectedIdsArray.length >= maxSelection &&
            !selected;

          return (
            <FileCard
              key={file.id}
              file={file}
              selectionMode={selectionEnabled ? (selectionMode === 'multiple' ? 'multiple' : 'single') : undefined}
              isSelected={selectionEnabled ? selected : false}
              isDisabled={isFileDisabled}
              onSelect={selectionEnabled ? () => handleSelect(file.id) : undefined}
              onToggleSelect={selectionEnabled ? handleToggleSelect : undefined}
              onRemove={shouldShowRemove ? () => onRemove?.(file.id) : undefined}
              showSize={showSize}
              showSource={showSource}
              showErrors={showErrors}
              formatSize={customFormatSize}
              dataTestId={dataTestId ? `${dataTestId}-file-${file.id}` : undefined}
            />
          );
        })}
      </FileList>
    </Container>
  );
};

ReviewFiles.displayName = 'ReviewFiles';

export default ReviewFiles;
